import { useState } from "react";
import EntityManager from "@/components/EntityManager";
import StatusBadge from "@/components/StatusBadge";
import VehicleRenewalDialog from "@/components/VehicleRenewalDialog";
import { VehicleDocsDialog, VEHICLE_DOC_TYPES } from "@/components/VehicleDocsDialog";
import { ExternalLink, History, Printer, FileDown, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { printVehicleDocRegister, printCompanyVehicles } from "@/lib/print";

const vehiclesExcel = (list) => {
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const headers = ["Sl No.", "Branch", "Registration No.", "Fleet Code", "Type", "Model", "Model Type", "Capacity (t)", "Status", "FASTag No.", "GPS Device ID", "GPS Provider", "FC Expiry", "Insurance Expiry", "PUC Expiry", "Permit Expiry"];
  const lines = [headers.join(",")];
  (list || []).forEach((r, i) => lines.push([i + 1, r.branch || "", r.registration_no || "", r.vehicle_no || "", r.type || "", r.model || "", r.model_type || "", r.capacity_tons || "", r.status || "", r.fastag_no || "", r.gps_id || "", r.gps_provider || "", r.fc_expiry || "", r.insurance_expiry || "", r.puc_expiry || "", r.permit_expiry || ""].map(cell).join(",")));
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "company-vehicles.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

const TrackCell = ({ label, id, link }) => {
  if (!id && !link) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {id && <span className="text-sm">{id}</span>}
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          data-testid={`${label}-track-link`}
        >
          Track <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
};

export default function Vehicles() {
  const [logVehicle, setLogVehicle] = useState(null);
  const [docsVehicle, setDocsVehicle] = useState(null);
  return (
    <>
    <EntityManager
      rowActions={(row) => (
        <>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Upload / view documents (RC, Insurance, PUC, Permit, DL)" onClick={() => setDocsVehicle(row)} data-testid="vehicle-docs-btn">
            <FileCheck2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Doc renewal history" onClick={() => setLogVehicle(row)} data-testid="vehicle-renewal-history-btn">
            <History className="h-4 w-4" />
          </Button>
        </>
      )}
      headerExtra={(rows, displayRows) => (
        <>
          <Button variant="outline" onClick={() => vehiclesExcel(displayRows?.length ? displayRows : rows)} disabled={!rows?.length} data-testid="vehicles-excel-btn">
            <FileDown className="mr-2 h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => printCompanyVehicles(displayRows?.length ? displayRows : rows)} disabled={!rows?.length} data-testid="vehicles-print-btn">
            <Printer className="mr-2 h-4 w-4" /> Print
          </Button>
          <Button variant="outline" onClick={() => printVehicleDocRegister(rows)} disabled={!rows?.length} data-testid="vehicle-doc-register-btn">
            <Printer className="mr-2 h-4 w-4" /> Doc Register
          </Button>
        </>
      )}
      title="Company Vehicles"
      subtitle="Your fleet of trucks and carriers."
      endpoint="vehicles"
      entityName="Vehicle"
      filters={[
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "Registration / fleet no / model / FASTag",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            return [r.registration_no, r.vehicle_no, r.branch, r.type, r.model, r.status, r.fastag_no, r.gps_id, r.gps_provider, r.eway_bill_no]
              .some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
      ]}
      defaults={{ registration_no: "", vehicle_no: "", branch: "", type: "Truck", model: "", model_type: "", capacity_tons: 0, status: "Available", fastag_no: "", fastag_link: "", gps_id: "", gps_link: "", gps_provider: "", eway_bill_no: "", eway_link: "", eway_valid_upto: "", fc_expiry: "", insurance_expiry: "", puc_expiry: "", permit_expiry: "" }}
      fields={[
        { key: "registration_no", label: "Registration No.", type: "text", placeholder: "MH12 AB 1234" },
        { key: "vehicle_no", label: "Vehicle no. (fleet code)", type: "text", placeholder: "NTC-V01" },
        { key: "branch", label: "Branch", type: "select", options: ["Chennai", "Tada", "Hyderabad", "Jaipur"], placeholder: "Select branch" },
        { key: "type", label: "Type", type: "select", options: ["Truck", "Trailer", "Container", "Tanker", "Mini Truck"] },
        { key: "model", label: "Model", type: "text", placeholder: "Tata LPT 3118" },
        { key: "model_type", label: "Model Type", type: "text", placeholder: "e.g. 32 Feet SXL / 6 Wheel" },
        { key: "capacity_tons", label: "Capacity (tons)", type: "number", placeholder: "25" },
        { key: "status", label: "Status", type: "select", options: ["Available", "On Trip", "Maintenance"] },
        { key: "fastag_no", label: "FASTag No. (Fast Tracking)", type: "text", placeholder: "FASTag / toll tag number" },
        { key: "fastag_link", label: "FASTag Tracking Link", type: "text", placeholder: "https://... FASTag portal URL" },
        { key: "gps_id", label: "GPS Device ID", type: "text", placeholder: "GPS device / IMEI" },
        { key: "gps_provider", label: "GPS Provider Name", type: "text", placeholder: "e.g. Vamosys, LocoNav, Fleetx" },
        { key: "gps_link", label: "GPS Tracking Link", type: "text", placeholder: "https://... GPS portal URL" },
        { key: "eway_bill_no", label: "E-Way Bill No.", type: "text", placeholder: "12-digit EWB number" },
        { key: "eway_link", label: "E-Way Bill Link", type: "text", placeholder: "https://ewaybillgst.gov.in/... (optional)" },
        { key: "eway_valid_upto", label: "E-Way Bill Valid Upto", type: "date" },
        { key: "fc_expiry", label: "FC expiry", type: "date" },
        { key: "insurance_expiry", label: "Insurance expiry", type: "date" },
        { key: "puc_expiry", label: "PUC expiry", type: "date" },
        { key: "permit_expiry", label: "Permit expiry", type: "date" },
      ]}
      columns={[
        { key: "_sl", label: "SL No.", render: (r, idx) => (typeof idx === "number" ? idx + 1 : "—") },
        { key: "branch", label: "Branch", render: (r) => r.branch || "—" },
        { key: "registration_no", label: "Registration No." },
        { key: "type", label: "Type" },
        { key: "model", label: "Model" },
        { key: "model_type", label: "Model Type", render: (r) => r.model_type || "—" },
        { key: "_docs", label: "Documents", render: (r) => {
          const n = Object.keys(r.documents || {}).length;
          return (
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${n ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`} data-testid="vehicle-doc-count">
              <FileCheck2 className="h-3.5 w-3.5" /> {n}/{VEHICLE_DOC_TYPES.length}
            </span>
          );
        } },
        { key: "capacity_tons", label: "Capacity (t)", align: "right" },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
        { key: "fastag", label: "Fast Tracking (FASTag)", render: (r) => <TrackCell label="fastag" id={r.fastag_no} link={r.fastag_link} /> },
        { key: "gps", label: "GPS Tracking", render: (r) => <TrackCell label="gps" id={r.gps_id} link={r.gps_link} /> },
        {
          key: "link_status",
          label: "Tracking Status",
          render: (r) =>
            r.gps_link ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700" data-testid="gps-linked-badge">
                GPS Linked
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500" data-testid="gps-notlinked-badge">
                Not Linked
              </span>
            ),
        },
      ]}
    />
    <VehicleRenewalDialog vehicle={logVehicle} onClose={() => setLogVehicle(null)} />
    <VehicleDocsDialog vehicle={docsVehicle} onClose={() => setDocsVehicle(null)} />
    </>
  );
}
